// The Tracker is a layer/bridge to assist with stats tracking. Either via Google Analytics 4 functions or via communication with our native apps via a triggered link click.
// See the partial at `app/views/layouts/shared/analytics/_google_analytics.html.erb` for the window object helper functions that are referenced in the wrappers in this class.

import {stringify} from 'qs';


export default class Tracker {

    /**
     * @param {Object} win - The global environment object on which our tracking functions are assigned (usually `window`).
     * @param {Object} doc - The document object (usually `document`) so we can use methods and the DOM.
     * @param {Object} [props] - Optional configuration properties.
     * @param {boolean} [props.isNativeApp] - Tracking for native apps is handled separately using this `boolean` for branching.
     */
    constructor(win, doc, props = {}) {
        this.win = win;
        this.doc = doc;
        this.isNativeApp = props.hasOwnProperty('isNativeApp') ? props['isNativeApp'] : false;
        this.nativeAppPlatform = props.hasOwnProperty('nativeAppPlatform') ? props['nativeAppPlatform'] : null;
        this.nativeAppVersion = props.hasOwnProperty('nativeAppVersion') ? props['nativeAppVersion'] : null;
        this.nativeAppUrlScheme = props.hasOwnProperty('nativeAppUrlScheme') ? props['nativeAppUrlScheme'] : null;
        this.nativeEventHost = props.hasOwnProperty('nativeEventHost') ? props['nativeEventHost'] : null;
        this.nativeEventEndpoint = props.hasOwnProperty('nativeEventEndpoint') ? props['nativeEventEndpoint'] : null;

        // For native app we use our own function from this class.
        // For web apps we look on the `win` object to find the tracking helper functions (may be included inline within the HTML page's head).
        this.pageTracker = this.isNativeApp ? this.nativeAppPageTracker : this.webAppPageTracker;
        this.eventTracker = this.isNativeApp ? this.nativeAppEventTracker : this.webAppEventTracker;
        this.dataLayerTrack = this.isNativeApp ? this.nativeAppDataLayerTrack : this.webAppDataLayerTrack;

        // For native apps we need more…
        this.nativeAppLinkElement = this.initLinkElement();

        this.eventNames = this.isNativeApp ? this.nativeAppEventNames() : this.webAppEventNames();
    }

    nativeAppPageTracker(url) {
        console.warn('Native app tracking of a Page URL is not supported', url);
    }

    // Native app event tracking URLs are not compatible with older versions of the native apps.
    // A `nativeEventEndpoint` will only be set when the app version is compatible.
    // Therefore a url will only be generated (and a `click` triggered) for compatible versions of the app.
    nativeAppEventTracker(eventName, options) {
        let url = this.getNativeAppEventUrl(eventName, options);
        if (url) {
            if (!this.nativeAppLinkElement) return;
            // The click event is triggered within a setTimeout callback.
            // This fixes an issue where this new 'click' event would prevent any default action from happening within the Android native app webview.
            this.win.setTimeout(() => {
                this.nativeAppLinkElement.setAttribute('href', url);
                this.nativeAppLinkElement.click();
                console.log('[Tracker] nativeAppEventTracker: ' + this.nativeAppLinkElement.getAttribute('href'));
            }, 10);
        }
    }

    nativeAppDataLayerTrack(data) {
        console.warn('Native app tracking via Data Layers is not supported', data);
    }

    webAppPageTracker(url) {
        console.warn('Web app tracking of a Page URL is not supported', url);
    }

    webAppDataLayerTrack(data) {
        console.warn('Web app tracking via Data Layers is not supported', data);
    }

    webAppEventTracker(eventName, options = {}) {
        if (this.win.RankersAnalytics && this.win.RankersAnalytics.eventTracker) {
            this.win.RankersAnalytics.eventTracker(eventName, options)
        }
    }

    nativeAppEventNames() {
        if (this.win.RankersAnalytics && this.win.RankersAnalytics.EventName) {
            return this.win.RankersAnalytics.EventName;
        }
    }

    webAppEventNames() {
        if (this.win.RankersAnalytics && this.win.RankersAnalytics.EventName) {
            return this.win.RankersAnalytics.EventName;
        }
    }

    // helpers

    // Build (or reuse) a hidden anchor tag with a URL to communicate to the native app and insert it into the DOM.
    // A single shared anchor is reused across every Tracker instance: the anchor carries no per-instance state
    // (its `href` is set just-in-time before each click), so creating one per instance only leaks detached DOM
    // nodes. Reusing the first anchor found keeps the count at one no matter how many components construct a Tracker.
    initLinkElement(options = {}) {
        if (!this.isNativeApp) return null;
        let element = this.doc.querySelector('.tracker__nativeAppLink');
        if (element) return element;
        element = this.doc.createElement('a');
        element.setAttribute("class", `tracker__nativeAppLink`);
        element.setAttribute("style", `position: absolute; top: 100%; opacity: 0;`);
        element.setAttribute("href", "javascript:void(0);");
        element.innerHTML = '';
        this.doc.body.insertBefore(element, null);
        return element;
    }

    getNativeAppEventUrl(eventName, options = {}) {
        if (!this.nativeAppUrlScheme || !this.nativeEventHost || !this.nativeEventEndpoint) return;

        const scheme = this.nativeAppUrlScheme;
        const host = this.nativeEventHost;
        const path = this.nativeEventEndpoint;

        const baseParams = {eventName: eventName};
        const backwardsCompatibilityParams = getBackwardsCompatibilityParams(eventName, options);
        const params = {...baseParams, ...options, ...backwardsCompatibilityParams};
        const queryString = stringify(params, {
                addQueryPrefix: true,
                indices: false,
                arrayFormat: 'brackets',
                sort: false
            }
        )

        const url = `${scheme}://${host}${path}${queryString}`;
        return url;
    }

}

// Backwards compatibility with Native Apps (prior to 2022-06 / iOS < 2.16.17 / Android < 3.18.23)
// Prior to 2022-06 the new event url might look something like:
// campingnz://www.rankers.co.nz/online/apps/camping_nz/to_native/events/new?eventCategory=FOO&eventAction=BAR&eventLabel=BAZ&eventValue=FAR
// (this was due to Google Universal Analytics only using those parameters for an event (category, action, label, value)).
//
// The old native apps parse these query string parameters.
// Only the `eventAction` and `eventLabel` options were used within the native app to send data to Firebase Analytics.
// However they do need the `eventCategory` too because they log the value.
//
// For backwards compat, the `eventLabel` option must be passed into the `eventTracker` method as an option too (it must be present).
// So when the `eventLabel` option is present we also set the `eventAction` option to the `eventName` value.
// Now the old native apps see the `eventAction` and `eventLabel` params and send these (as they always have) to Firebase Analytics.
// Within these old native apps the `eventAction` becomes the event's name and `eventLabel` becomes the value for the `item_id` custom parameter.
//
// Within the new native apps the `eventName` becomes the event's name and the other params are sent as custom parameters,
// EXCEPT the four UA keys (`eventName`, `eventCategory`, `eventAction`, `eventLabel`), which both apps strip. So `eventLabel`
// never reaches Firebase from a new app. See doc/README--analytics.md → "Backward Compatibility with Older Native Apps".
function getBackwardsCompatibilityParams(eventName, options = {}) {
    if (typeof options['eventLabel'] === 'undefined' || options['eventLabel'] === null || options['eventLabel'] === '') return {};
    return {eventAction: eventName, eventCategory: 'COMPAT'};
}
